import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ChatWidget } from "@/components/ChatWidget";
import { FileUploadZone, AttachmentList, AttachmentBadge } from "@/components/FileUpload";
import { DateSelectionCalendar } from "@/components/DateSelectionCalendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Users, FolderKanban, Clock, Download, Plus, Trash2, Edit,
  CalendarIcon, Filter, Search, Loader2, Building2, Send, UserPlus,
  FileText, Paperclip, MessageSquare, CheckCircle, XCircle, AlertCircle,
  Building, DollarSign, CreditCard, FileSignature, MapPin, Lock, Unlock,
  History, FileSpreadsheet, BarChart3, StickyNote, Eye, Upload, Globe
} from "lucide-react";

// Import extracted admin components
import { AdminOverview, AdminAgreements, AdminMessages, AdminContractorProfile } from "./admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Workers Management with Invitation System
const WorkersManagement = () => {
  const [workers, setWorkers] = useState([]);
  const [inactiveWorkers, setInactiveWorkers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [creationMethod, setCreationMethod] = useState("direct"); // "direct" or "invite"
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    phone: "",
    personal_number: "",
    address: "",
    user_type: "worker",
    is_manpower_worker: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workersRes, inactiveRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/users/inactive`),
        axios.get(`${API}/invitations`)
      ]);
      setWorkers(workersRes.data);
      setInactiveWorkers(inactiveRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!window.confirm(`Deactivate ${userName}? They will no longer be able to log in, but their timesheet history will be preserved.`)) return;
    try {
      await axios.put(`${API}/users/${userId}/deactivate`);
      toast.success(`${userName} has been deactivated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const handleReactivate = async (userId, userName) => {
    try {
      await axios.put(`${API}/users/${userId}/reactivate`);
      toast.success(`${userName} has been reactivated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (creationMethod === "direct") {
        // Direct creation with password
        await axios.post(`${API}/users/create-direct`, formData);
        toast.success("Team member created! They will receive a notification email.");
      } else {
        // Invitation flow (kept as backup)
        const { password, ...inviteData } = formData;
        await axios.post(`${API}/invitations/create`, inviteData);
        toast.success("Invitation sent! They will receive an email to set up their password.");
      }
      setDialogOpen(false);
      setFormData({
        email: "",
        name: "",
        password: "",
        phone: "",
        personal_number: "",
        address: "",
        user_type: "worker",
        is_manpower_worker: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team member");
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await axios.post(`${API}/invitations/resend/${invitationId}`);
      toast.success("Invitation resent successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm("Are you sure you want to cancel this invitation?")) return;
    try {
      await axios.delete(`${API}/invitations/${invitationId}`);
      toast.success("Invitation cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const handleDelete = async (id, userName) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE "${userName}"?\n\n` +
      `This will permanently delete this team member and ALL their associated data (timesheets, assignments, etc.).\n\n` +
      `This action CANNOT be undone.\n\n` +
      `If you just want to revoke their access while keeping their history, use "Remove Access" instead.`
    );
    if (!confirmed) return;
    
    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      `Are you absolutely sure? Type confirmation:\n\n` +
      `You are about to permanently delete ${userName} and all their data.`
    );
    if (!doubleConfirm) return;
    
    try {
      await axios.delete(`${API}/users/${id}?confirm_delete_timesheets=true`);
      toast.success(`${userName} permanently deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete team member");
    }
  };

  const toggleManpower = async (id) => {
    try {
      await axios.put(`${API}/users/${id}/toggle-manpower`);
      fetchData();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredWorkers = workers.filter(w => 
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="workers-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">TEAM MEMBERS</h1>
          <p className="text-[#52525B]">Manage all team members and contractors</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-worker-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl">ADD NEW TEAM MEMBER</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Method Toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    creationMethod === "direct" 
                      ? "bg-white shadow text-[#F37021]" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  onClick={() => setCreationMethod("direct")}
                >
                  Set Password Directly
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    creationMethod === "invite" 
                      ? "bg-white shadow text-[#F37021]" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  onClick={() => setCreationMethod("invite")}
                >
                  Send Invite Link
                </button>
              </div>
              
              {creationMethod === "direct" ? (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    You set the password directly. The user will receive a notification email with the login URL.
                    They can change their password after logging in.
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    An invitation email will be sent. The user will set their own password when they activate their account.
                  </p>
                </div>
              )}
              
              <div>
                <Label className="input-label">Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="worker-name-input"
                />
              </div>
              <div>
                <Label className="input-label">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="worker-email-input"
                />
              </div>
              
              {creationMethod === "direct" && (
                <div>
                  <Label className="input-label">Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Min 6 characters"
                    data-testid="worker-password-input"
                  />
                </div>
              )}
              
              <div>
                <Label className="input-label">Personal Number</Label>
                <Input
                  value={formData.personal_number}
                  onChange={(e) => setFormData({ ...formData, personal_number: e.target.value })}
                  placeholder="YYYYMMDD-XXXX"
                  data-testid="worker-personal-number-input"
                />
              </div>
              <div>
                <Label className="input-label">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="worker-phone-input"
                />
              </div>
              <div>
                <Label className="input-label">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="worker-address-input"
                />
              </div>
              <div>
                <Label className="input-label">User Type</Label>
                <Select
                  value={formData.user_type}
                  onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                >
                  <SelectTrigger data-testid="worker-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Team Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="manpower"
                  checked={formData.is_manpower_worker}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_manpower_worker: checked })}
                  data-testid="worker-manpower-checkbox"
                />
                <Label htmlFor="manpower" className="text-sm">Manpower Team Member (Gavs Bemanning AB)</Label>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="submit-worker-button">
                {creationMethod === "direct" ? "Create Team Member" : "Send Invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardHeader className="py-3">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
              <MessageSquare className="w-5 h-5" />
              Pending Invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-white rounded border border-yellow-200">
                  <div>
                    <p className="font-medium">{inv.name}</p>
                    <p className="text-sm text-gray-500">{inv.email}</p>
                    <p className="text-xs text-gray-400">
                      Expires: {new Date(inv.expires_at).toLocaleString()}
                      {inv.expired && <span className="ml-2 text-red-500 font-semibold">EXPIRED</span>}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleResendInvitation(inv.id)}
                      data-testid={`resend-invite-${inv.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Resend
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleCancelInvitation(inv.id)}
                      data-testid={`cancel-invite-${inv.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
        <Input
          placeholder="Search team members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-workers-input"
        />
      </div>

      {/* Workers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : (
            <Table className="table-aggreko">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Manpower</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.map((worker) => (
                  <TableRow key={worker.id}>
                    <TableCell className="font-medium">{worker.name}</TableCell>
                    <TableCell>{worker.email}</TableCell>
                    <TableCell>{worker.phone || "-"}</TableCell>
                    <TableCell>
                      <Badge className={
                        worker.user_type === "admin" ? "badge-admin" :
                        worker.user_type === "contractor" ? "badge-contractor" :
                        "badge-worker"
                      }>
                        {worker.user_type === "worker" ? "Team Member" : worker.user_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={worker.is_manpower_worker}
                        onCheckedChange={() => toggleManpower(worker.id)}
                        disabled={worker.user_type !== "worker"}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {worker.user_type === "worker" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-gray-700 text-xs"
                            onClick={() => handleDeactivate(worker.id, worker.name)}
                            title="Remove Access (keeps history)"
                            data-testid={`deactivate-worker-${worker.id}`}
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            Remove Access
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 text-xs"
                          onClick={() => handleDelete(worker.id, worker.name)}
                          title="Delete Permanently"
                          data-testid={`delete-worker-${worker.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredWorkers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-[#A1A1AA]">
                      No team members found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Timesheets Management with Excel Export, Monthly Summary, Locking & Audit Logs
const TimesheetsManagement = () => {
  const [activeTab, setActiveTab] = useState("timesheets"); // timesheets, summary, locking, audit
  const [timesheets, setTimesheets] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState("excel");
  const [filters, setFilters] = useState({
    worker_id: "all",
    a_number: "all",
    start_date: null,
    end_date: null
  });
  
  // Cascading filter options
  const [filterOptions, setFilterOptions] = useState({
    a_numbers: [],
    workers: []
  });

  // Monthly Summary state
  const [summaryMonth, setSummaryMonth] = useState(new Date().getMonth() + 1);
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Locking state
  const [locks, setLocks] = useState([]);
  const [lockMonth, setLockMonth] = useState(new Date().getMonth() + 1);
  const [lockYear, setLockYear] = useState(new Date().getFullYear());
  const [lockLoading, setLockLoading] = useState(false);

  // Audit Log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedTimesheetAudit, setSelectedTimesheetAudit] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch cascading filter options when filters change
  useEffect(() => {
    fetchFilterOptions();
  }, [filters.a_number, filters.worker_id, filters.start_date, filters.end_date]);

  const fetchFilterOptions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));
      
      const response = await axios.get(`${API}/timesheets/filter-options?${params.toString()}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Failed to load filter options:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [tsRes, workRes, agreeRes, locksRes] = await Promise.all([
        axios.get(`${API}/timesheets`),
        axios.get(`${API}/users`),
        axios.get(`${API}/agreements`),
        axios.get(`${API}/timesheet-locks`)
      ]);
      setTimesheets(tsRes.data);
      setWorkers(workRes.data.filter(w => w.user_type === "worker"));
      setAgreements(agreeRes.data);
      setLocks(locksRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));
      
      const response = await axios.get(`${API}/timesheets?${params.toString()}`);
      setTimesheets(response.data);
    } catch (error) {
      toast.error("Failed to filter timesheets");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/timesheets/export/pdf?${params.toString()}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `timesheets_${format(new Date(), "yyyyMMdd")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exported successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const exportExcelOrCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.append("format", exportFormat);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/timesheets/export/excel?${params.toString()}`, {
        responseType: "blob"
      });

      const ext = exportFormat === "excel" ? "xlsx" : "csv";
      const mimeType = exportFormat === "excel" 
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        : "text/csv";
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `timesheets_${format(new Date(), "yyyyMMdd")}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${exportFormat.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${exportFormat.toUpperCase()}`);
    }
  };

  // Monthly Summary
  const fetchMonthlySummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.get(`${API}/reports/monthly-summary?month=${summaryMonth}&year=${summaryYear}`);
      setSummaryData(response.data);
    } catch (error) {
      toast.error("Failed to load monthly summary");
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryMonth, summaryYear]);

  // Locking
  const handleLockPeriod = async (lock = true) => {
    setLockLoading(true);
    try {
      await axios.post(`${API}/timesheet-locks`, {
        month: lockMonth,
        year: lockYear,
        locked: lock
      });
      toast.success(`Period ${lockYear}-${String(lockMonth).padStart(2, '0')} ${lock ? 'locked' : 'unlocked'} successfully`);
      // Refresh locks
      const locksRes = await axios.get(`${API}/timesheet-locks`);
      setLocks(locksRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lock");
    } finally {
      setLockLoading(false);
    }
  };

  // Audit Logs
  const fetchAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/timesheet-audit`);
      setAuditLogs(response.data);
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const viewTimesheetAudit = async (timesheetId) => {
    try {
      const response = await axios.get(`${API}/timesheet-audit/${timesheetId}`);
      setSelectedTimesheetAudit(response.data);
      setAuditDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load audit history");
    }
  };

  useEffect(() => {
    if (activeTab === "summary") {
      fetchMonthlySummary();
    } else if (activeTab === "audit") {
      fetchAuditLogs();
    }
  }, [activeTab, fetchMonthlySummary, fetchAuditLogs]);

  const isLocked = (month, year) => {
    return locks.some(l => l.month === month && l.year === year && l.locked);
  };

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div data-testid="timesheets-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">TIMESHEETS</h1>
          <p className="text-[#52525B]">View, export, lock and manage timesheet entries</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-2">
        <Button 
          variant={activeTab === "timesheets" ? "default" : "ghost"}
          className={activeTab === "timesheets" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("timesheets")}
          data-testid="tab-timesheets"
        >
          <Clock className="w-4 h-4 mr-2" />
          Timesheets
        </Button>
        <Button 
          variant={activeTab === "summary" ? "default" : "ghost"}
          className={activeTab === "summary" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("summary")}
          data-testid="tab-summary"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Monthly Summary
        </Button>
        <Button 
          variant={activeTab === "locking" ? "default" : "ghost"}
          className={activeTab === "locking" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("locking")}
          data-testid="tab-locking"
        >
          <Lock className="w-4 h-4 mr-2" />
          Period Locking
        </Button>
      </div>

      {/* TIMESHEETS TAB */}
      {activeTab === "timesheets" && (
        <>
          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button className="btn-primary" onClick={exportPDF} data-testid="export-pdf-button">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <div className="flex items-center gap-2">
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-24" data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={exportExcelOrCSV} data-testid="export-excel-button">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="input-label text-xs">A-Number</Label>
                  <Select
                    value={filters.a_number}
                    onValueChange={(value) => setFilters({ ...filters, a_number: value })}
                  >
                    <SelectTrigger data-testid="filter-a-number">
                      <SelectValue placeholder="All A-Numbers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All A-Numbers</SelectItem>
                      {/* Show only A-numbers that have entries matching current filters */}
                      {filterOptions.a_numbers.length > 0 ? (
                        filterOptions.a_numbers.map((a) => (
                          <SelectItem key={a.a_number} value={a.a_number}>
                            {a.a_number}{a.project_name ? ` - ${a.project_name}` : ''} ({a.entry_count})
                          </SelectItem>
                        ))
                      ) : (
                        // Fallback to all agreements if no filter options loaded yet
                        agreements.map((a) => (
                          <SelectItem key={a.id} value={a.a_number}>{a.a_number} - {a.project_name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label text-xs">Team Member</Label>
                  <Select
                    value={filters.worker_id}
                    onValueChange={(value) => setFilters({ ...filters, worker_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Team Members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Team Members</SelectItem>
                      {/* Show only workers that have entries matching current filters */}
                      {filterOptions.workers.length > 0 ? (
                        filterOptions.workers.map((w) => (
                          <SelectItem key={w.worker_id} value={w.worker_id}>
                            {w.worker_name} ({w.entry_count})
                          </SelectItem>
                        ))
                      ) : (
                        // Fallback to all workers if no filter options loaded yet
                        workers.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label text-xs">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.start_date ? format(filters.start_date, "PPP") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.start_date}
                        onSelect={(date) => setFilters({ ...filters, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="input-label text-xs">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.end_date ? format(filters.end_date, "PPP") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.end_date}
                        onSelect={(date) => setFilters({ ...filters, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <Button onClick={applyFilters} className="mt-4 btn-secondary" data-testid="apply-filters-button">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </CardContent>
          </Card>

          {/* Timesheets Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
                </div>
              ) : (
                <Table className="table-aggreko">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Team Member</TableHead>
                      <TableHead>A-Number</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Lock Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timesheets.map((ts) => (
                      <TableRow key={ts.id} className={ts.is_locked ? "bg-gray-50" : ""}>
                        <TableCell>{ts.date}</TableCell>
                        <TableCell className="font-medium">{ts.worker_name}</TableCell>
                        <TableCell>
                          {ts.a_number ? (
                            <Badge variant="outline" className="text-xs">{ts.a_number}</Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>{ts.agreement_name || ts.a_number}</TableCell>
                        <TableCell>{ts.start_time} - {ts.end_time}</TableCell>
                        <TableCell>{ts.location}</TableCell>
                        <TableCell className="max-w-[100px]">
                          {ts.notes ? (
                            <span className="flex items-center gap-1 text-xs text-blue-600" title={ts.notes}>
                              <StickyNote className="w-3 h-3" />
                              {ts.notes.length > 20 ? ts.notes.substring(0, 20) + "..." : ts.notes}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {ts.is_locked ? (
                            <Badge className="bg-red-100 text-red-700">
                              <Lock className="w-3 h-3 mr-1" />
                              Locked
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">
                              <Unlock className="w-3 h-3 mr-1" />
                              Open
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {timesheets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#A1A1AA]">
                          No timesheets found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* MONTHLY SUMMARY TAB */}
      {activeTab === "summary" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#F37021]" />
                Monthly Summary Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <Label className="text-xs">Month</Label>
                  <Select value={String(summaryMonth)} onValueChange={(v) => setSummaryMonth(parseInt(v))}>
                    <SelectTrigger className="w-32" data-testid="summary-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Year</Label>
                  <Select value={String(summaryYear)} onValueChange={(v) => setSummaryYear(parseInt(v))}>
                    <SelectTrigger className="w-24" data-testid="summary-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={fetchMonthlySummary} className="btn-primary" data-testid="generate-summary">
                    Generate Report
                  </Button>
                </div>
              </div>

              {summaryLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
                </div>
              ) : summaryData ? (
                <div className="space-y-4">
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-lg font-bold text-[#333333]">
                      {months.find(m => m.value === summaryData.month)?.label} {summaryData.year}
                    </p>
                    <p className="text-2xl font-bold text-[#F37021]">
                      Total: {summaryData.total_hours.toFixed(1)} hours
                    </p>
                  </div>

                  {summaryData.workers.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team Member</TableHead>
                          <TableHead>A-Number(s)</TableHead>
                          <TableHead className="text-right">Total Hours</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summaryData.workers.map((w) => (
                          <TableRow key={w.worker_id}>
                            <TableCell className="font-medium">{w.worker_name}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {w.a_numbers.map((a) => (
                                  <div key={`${w.worker_id}-${a.a_number}`} className="flex justify-between text-sm">
                                    <span className="text-gray-600">{a.a_number}: {a.project_name}</span>
                                    <span className="font-medium">{a.total_hours.toFixed(1)}h</span>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-bold text-[#F37021]">
                              {w.total_hours.toFixed(1)}h
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-gray-500 py-8">No timesheets for this period</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Select a period and generate report</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* PERIOD LOCKING TAB */}
      {activeTab === "locking" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#F37021]" />
                Period Locking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-6">
                Lock a period to prevent team members from editing timesheets. Contractors can unlock periods if needed.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <Label className="text-xs">Month</Label>
                  <Select value={String(lockMonth)} onValueChange={(v) => setLockMonth(parseInt(v))}>
                    <SelectTrigger className="w-32" data-testid="lock-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Year</Label>
                  <Select value={String(lockYear)} onValueChange={(v) => setLockYear(parseInt(v))}>
                    <SelectTrigger className="w-24" data-testid="lock-year">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button 
                    onClick={() => handleLockPeriod(true)} 
                    disabled={lockLoading || isLocked(lockMonth, lockYear)}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="lock-period-btn"
                  >
                    {lockLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Lock Period
                  </Button>
                  <Button 
                    onClick={() => handleLockPeriod(false)} 
                    disabled={lockLoading || !isLocked(lockMonth, lockYear)}
                    variant="outline"
                    data-testid="unlock-period-btn"
                  >
                    {lockLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                    Unlock Period
                  </Button>
                </div>
              </div>

              {/* Locked Periods List */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Currently Locked Periods</h3>
                {locks.filter(l => l.locked).length > 0 ? (
                  <div className="space-y-2">
                    {locks.filter(l => l.locked).map((l) => (
                      <div key={`${l.year}-${l.month}`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-3">
                          <Lock className="w-5 h-5 text-red-600" />
                          <div>
                            <p className="font-medium">{months.find(m => m.value === l.month)?.label} {l.year}</p>
                            <p className="text-xs text-gray-500">Locked by {l.locked_by_name} on {new Date(l.locked_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No periods are currently locked</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Worker Requests Management (NEW!)
const WorkerRequestsManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  const [inductionDate, setInductionDate] = useState(null);
  const [requestType, setRequestType] = useState("contractor"); // "contractor" or "internal"
  const [downloadingQuote, setDownloadingQuote] = useState(null);
  const [requestAttachments, setRequestAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [directAssignedWorkers, setDirectAssignedWorkers] = useState([]);
  // New date selection state
  const [dateSelectionMode, setDateSelectionMode] = useState("consecutive"); // "consecutive" or "custom"
  const [customSelectedDates, setCustomSelectedDates] = useState([]);
  // PO Number state
  const [editingPO, setEditingPO] = useState(false);
  const [poValue, setPOValue] = useState("");
  const [savingPO, setSavingPO] = useState(false);
  const [poHistoryDialog, setPOHistoryDialog] = useState(false);
  const [poHistory, setPOHistory] = useState([]);
  const [loadingPOHistory, setLoadingPOHistory] = useState(false);
  // Filter state
  const [filterWithoutPO, setFilterWithoutPO] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Memoize filtered requests - universal search across all fields
  const filteredRequests = useMemo(() => {
    let result = requests;
    
    if (filterWithoutPO) {
      result = result.filter(r => !r.po_number);
    }
    
    if (searchQuery) {
      const search = searchQuery.toLowerCase().trim();
      result = result.filter(r => {
        // Search across multiple fields
        const searchableFields = [
          r.agreement_a_number,
          r.agreement_project_name,
          r.po_number,
          r.location_name,
          r.address,
          r.country,
          r.status,
          r.contact_person_onsite_name,
          r.description,
          r.notes,
          r.created_by_name,
          // Search in assigned workers array
          ...(r.assigned_worker_names || [])
        ];
        
        return searchableFields.some(field => 
          field && field.toLowerCase().includes(search)
        );
      });
    }
    
    return result;
  }, [requests, filterWithoutPO, searchQuery]);
  
  const [formData, setFormData] = useState({
    num_workers: 1,
    num_days: 1,
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: "",
    work_on_weekends: false,
    date_selection_mode: "consecutive",
    custom_dates: [],
    country: "Sweden",
    agreement_id: "",
    address: "",
    location_name: "",
    contact_person_onsite_id: "",
    project_engineer_id: "",
    description: "",
    requirements: {
      id06_card: false,
      identification: false,
      ssg_card: false,
      induction_required: false,
      induction_details: {
        induction_type: "",
        when_needed: ""
      },
      other_requirements: ""
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, workersRes, agreementsRes, availableRes] = await Promise.all([
        axios.get(`${API}/worker-requests`),
        axios.get(`${API}/users`),
        axios.get(`${API}/agreements/active`),
        axios.get(`${API}/users/available-workers`)
      ]);
      setRequests(reqRes.data);
      setWorkers(workersRes.data);
      setAgreements(agreementsRes.data);
      setAvailableWorkers(availableRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      num_workers: 1,
      num_days: 1,
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: "",
      work_on_weekends: false,
      date_selection_mode: "consecutive",
      custom_dates: [],
      country: "Sweden",
      agreement_id: "",
      address: "",
      location_name: "",
      contact_person_onsite_id: "",
      project_engineer_id: "",
      description: "",
      requirements: {
        id06_card: false,
        identification: false,
        ssg_card: false,
        induction_required: false,
        induction_details: {
          induction_type: "",
          when_needed: ""
        },
        other_requirements: ""
      }
    });
    setSelectedDate(new Date());
    setEndDate(null);
    setInductionDate(null);
    setEditingRequest(null);
    setRequestType("contractor");
    setRequestAttachments([]);
    setDirectAssignedWorkers([]);
    setDateSelectionMode("consecutive");
    setCustomSelectedDates([]);
  };

  const openEditDialog = async (req) => {
    setEditingRequest(req);
    setFormData({
      num_workers: req.num_workers,
      num_days: req.num_days,
      start_date: req.start_date,
      end_date: req.end_date || "",
      work_on_weekends: req.work_on_weekends || false,
      date_selection_mode: req.date_selection_mode || "consecutive",
      custom_dates: req.custom_dates || [],
      country: req.country || "Sweden",
      agreement_id: req.agreement_id || "",
      address: req.address || "",
      location_name: req.location_name || "",
      contact_person_onsite_id: req.contact_person_onsite_id || "",
      project_engineer_id: req.project_engineer_id || "",
      description: req.description || "",
      requirements: req.requirements || {
        id06_card: false,
        identification: false,
        ssg_card: false,
        induction_required: false,
        induction_details: {
          induction_type: "",
          when_needed: ""
        },
        other_requirements: ""
      }
    });
    setSelectedDate(new Date(req.start_date));
    setEndDate(req.end_date ? new Date(req.end_date) : null);
    setDateSelectionMode(req.date_selection_mode || "consecutive");
    setCustomSelectedDates(req.custom_dates ? req.custom_dates.map(d => new Date(d)) : []);
    if (req.requirements?.induction_details?.when_needed) {
      setInductionDate(new Date(req.requirements.induction_details.when_needed));
    }
    
    // Fetch attachments for this request
    try {
      const attachmentsRes = await axios.get(`${API}/worker-requests/${req.id}/attachments`);
      setRequestAttachments(attachmentsRes.data || []);
    } catch (error) {
      console.error("Failed to load attachments");
      setRequestAttachments([]);
    }
    
    setDialogOpen(true);
  };
  
  const openViewDetails = async (req) => {
    setSelectedRequest(req);
    setPOValue(req.po_number || "");
    setEditingPO(false);
    
    // Fetch all attachments (request + agreement level)
    try {
      const attachmentsRes = await axios.get(`${API}/worker-requests/${req.id}/all-attachments`);
      setRequestAttachments([
        ...(attachmentsRes.data.request_attachments || []),
        ...(attachmentsRes.data.agreement_attachments || [])
      ]);
    } catch (error) {
      console.error("Failed to load attachments");
      setRequestAttachments([]);
    }
    
    setViewDetailsDialog(true);
  };
  
  // PO Number functions
  const handleSavePO = async () => {
    if (!selectedRequest) return;
    
    setSavingPO(true);
    try {
      const response = await axios.put(`${API}/worker-requests/${selectedRequest.id}/po`, {
        po_number: poValue
      });
      
      // Update local state
      setSelectedRequest(response.data);
      setRequests(requests.map(r => r.id === selectedRequest.id ? response.data : r));
      setEditingPO(false);
      toast.success("PO number saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save PO number");
    } finally {
      setSavingPO(false);
    }
  };
  
  const handleCancelPOEdit = () => {
    setPOValue(selectedRequest?.po_number || "");
    setEditingPO(false);
  };
  
  const openPOHistory = async () => {
    if (!selectedRequest) return;
    
    setLoadingPOHistory(true);
    setPOHistoryDialog(true);
    
    try {
      const response = await axios.get(`${API}/worker-requests/${selectedRequest.id}/po-history`);
      setPOHistory(response.data);
    } catch (error) {
      toast.error("Failed to load PO history");
      setPOHistory([]);
    } finally {
      setLoadingPOHistory(false);
    }
  };
  
  const handleFileUpload = async (files) => {
    if (!editingRequest) {
      toast.error("Please save the request first, then edit it to add files.");
      return;
    }
    
    setUploadingFiles(true);
    setUploadProgress(0);
    
    const totalFiles = files.length;
    let uploaded = 0;
    const newAttachments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Upload to backend
        const response = await axios.post(`${API}/worker-requests/${editingRequest.id}/attachments`, {
          filename: file.name,
          content_type: file.type,
          data: base64,
          description: ""
        });
        
        newAttachments.push(response.data);
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || "Unknown error"}`);
      }
    }
    
    if (newAttachments.length > 0) {
      setRequestAttachments([...requestAttachments, ...newAttachments]);
      toast.success(`${newAttachments.length} file(s) uploaded successfully`);
    }
    
    setUploadingFiles(false);
    setUploadProgress(0);
  };
  
  const handleAttachmentDelete = (attachmentId) => {
    setRequestAttachments(requestAttachments.filter(a => a.id !== attachmentId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate agreement is selected for new requests
    if (!editingRequest && !formData.agreement_id) {
      toast.error("Please select an A-number/Agreement first");
      return;
    }
    
    // Validate direct booking has workers selected
    if (requestType === "internal" && !editingRequest && directAssignedWorkers.length === 0) {
      toast.error("Please select at least one worker for direct booking");
      return;
    }
    
    // Validate custom dates if in custom mode
    if (dateSelectionMode === "custom" && customSelectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        start_date: dateSelectionMode === "custom" && customSelectedDates.length > 0
          ? format(customSelectedDates[0], "yyyy-MM-dd")
          : format(selectedDate, "yyyy-MM-dd"),
        end_date: dateSelectionMode === "custom" && customSelectedDates.length > 1
          ? format(customSelectedDates[customSelectedDates.length - 1], "yyyy-MM-dd")
          : (endDate ? format(endDate, "yyyy-MM-dd") : ""),
        work_on_weekends: formData.work_on_weekends || false,
        date_selection_mode: dateSelectionMode,
        custom_dates: dateSelectionMode === "custom" 
          ? customSelectedDates.map(d => format(d, "yyyy-MM-dd"))
          : [],
        country: formData.country || "Sweden",
        contact_person_onsite_id: formData.contact_person_onsite_id || null,
        project_engineer_id: formData.project_engineer_id || null,
        requirements: {
          ...formData.requirements,
          induction_details: formData.requirements.induction_required ? {
            ...formData.requirements.induction_details,
            when_needed: inductionDate ? format(inductionDate, "yyyy-MM-dd") : ""
          } : null
        },
        // Direct booking fields
        is_direct_booking: requestType === "internal" && !editingRequest,
        direct_assigned_workers: requestType === "internal" && !editingRequest ? directAssignedWorkers : []
      };
      
      if (editingRequest) {
        // Update existing request
        await axios.put(`${API}/worker-requests/${editingRequest.id}`, submitData);
        toast.success("Request updated successfully");
      } else {
        // Create new request
        const response = await axios.post(`${API}/worker-requests`, submitData);
        
        // Check for skipped workers feedback (Direct Booking)
        if (requestType === "internal" && response.data.skipped_workers?.length > 0) {
          // Some workers were skipped - show warning with details
          const skippedNames = response.data.skipped_workers.map(w => w.name).join(", ");
          toast.warning(
            `⚠️ ${response.data.booking_message || `Some workers were unavailable: ${skippedNames}`}`,
            { duration: 8000 }
          );
        } else if (requestType === "internal") {
          // All workers assigned successfully
          toast.success(response.data.booking_message || "Workers assigned directly! (Direct Booking)");
        } else {
          toast.success("Worker request sent to Gavs Bemanning AB");
        }
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingRequest ? "Failed to update request" : "Failed to create request"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await axios.delete(`${API}/worker-requests/${id}`);
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  const handleDownloadQuote = async (request) => {
    setDownloadingQuote(request.id);
    try {
      const response = await axios.get(`${API}/worker-requests/${request.id}/generate-quote`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `offert_${request.agreement_a_number || 'quote'}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename.replace(/\//g, '-'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate quote. Please ensure company rates are configured.");
    } finally {
      setDownloadingQuote(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500 text-white",
      confirmed: "bg-blue-500 text-white",
      workers_assigned: "bg-green-500 text-white",
      declined: "bg-red-500 text-white",
      completed: "bg-gray-500 text-white"
    };
    return styles[status] || "bg-gray-500 text-white";
  };

  const hasAnyRequirement = formData.requirements.id06_card || 
    formData.requirements.identification || 
    formData.requirements.ssg_card || 
    formData.requirements.induction_required ||
    formData.requirements.other_requirements;

  return (
    <div data-testid="worker-requests">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">TEAM MEMBER REQUESTS</h1>
          <p className="text-[#52525B]">Request team members from Gavs Bemanning AB</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="new-request-button" onClick={() => resetForm()}>
              <UserPlus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {editingRequest ? 'EDIT REQUEST' : 'REQUEST TEAM MEMBERS'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Request Type Selection */}
              {!editingRequest && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Label className="input-label text-sm mb-3 block">Request Type</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        requestType === "contractor" 
                          ? "border-[#F37021] bg-orange-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setRequestType("contractor")}
                    >
                      <p className="font-bold text-sm">External (Contractor)</p>
                      <p className="text-xs text-gray-500">Request from Gavs Bemanning AB</p>
                    </div>
                    <div 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        requestType === "internal" 
                          ? "border-[#F37021] bg-orange-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setRequestType("internal")}
                    >
                      <p className="font-bold text-sm">Internal Team Members</p>
                      <p className="text-xs text-gray-500">Assign existing team members</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Direct Booking Worker Selection - Only show for internal requests when creating */}
              {!editingRequest && requestType === "internal" && (
                <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <Label className="input-label text-sm text-green-700">
                      Direct Book Available Workers ({directAssignedWorkers.length} selected)
                    </Label>
                  </div>
                  <p className="text-xs text-green-600 mb-3">
                    Select workers to assign immediately. No Contractor approval needed.
                  </p>
                  
                  {availableWorkers.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p className="text-sm">No available workers found.</p>
                      <p className="text-xs">Workers may be hidden by the Contractor or not yet added.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {availableWorkers.map((worker) => (
                        <div 
                          key={worker.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                            directAssignedWorkers.includes(worker.id)
                              ? "border-green-500 bg-green-100"
                              : "border-gray-200 bg-white hover:border-green-300"
                          }`}
                          onClick={() => {
                            if (directAssignedWorkers.includes(worker.id)) {
                              setDirectAssignedWorkers(directAssignedWorkers.filter(id => id !== worker.id));
                            } else {
                              setDirectAssignedWorkers([...directAssignedWorkers, worker.id]);
                            }
                          }}
                          data-testid={`direct-book-worker-${worker.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              directAssignedWorkers.includes(worker.id) 
                                ? "bg-green-500 text-white" 
                                : "bg-gray-200 text-gray-600"
                            }`}>
                              {directAssignedWorkers.includes(worker.id) ? (
                                <CheckCircle className="w-5 h-5" />
                              ) : (
                                <Users className="w-4 h-4" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{worker.name}</p>
                              <p className="text-xs text-gray-500">{worker.email}</p>
                            </div>
                          </div>
                          <Badge className={worker.is_manpower_worker ? "badge-manpower" : "badge-worker"}>
                            {worker.is_manpower_worker ? "Manpower" : "Internal"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Number of Team Members</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.num_workers}
                    onChange={(e) => setFormData({ ...formData, num_workers: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Number of Days</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.num_days}
                    onChange={(e) => setFormData({ ...formData, num_days: parseInt(e.target.value) })}
                    required
                    disabled={dateSelectionMode === "custom" || !!endDate}
                  />
                  {(dateSelectionMode === "custom" || endDate) && (
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated from selected dates</p>
                  )}
                </div>
              </div>

              {/* Date Selection Mode Toggle */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="input-label mb-2 block">Date Selection Mode</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="date_selection_mode"
                      checked={dateSelectionMode === "consecutive"}
                      onChange={() => {
                        setDateSelectionMode("consecutive");
                        setCustomSelectedDates([]);
                      }}
                      className="w-4 h-4 text-[#F37021]"
                    />
                    <span className="text-sm font-medium">Consecutive Days</span>
                    <span className="text-xs text-gray-500">(Start → End date range)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="date_selection_mode"
                      checked={dateSelectionMode === "custom"}
                      onChange={() => {
                        setDateSelectionMode("custom");
                        setEndDate(null);
                      }}
                      className="w-4 h-4 text-[#F37021]"
                    />
                    <span className="text-sm font-medium">Custom Dates</span>
                    <span className="text-xs text-gray-500">(Pick specific days)</span>
                  </label>
                </div>
              </div>

              {/* Date Selection based on Mode */}
              {dateSelectionMode === "consecutive" ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="input-label">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="start-date-picker">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {format(selectedDate, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            if (date) {
                              setSelectedDate(date);
                              setFormData({ ...formData, start_date: format(date, "yyyy-MM-dd") });
                            }
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="input-label">End Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="end-date-picker">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {endDate ? format(endDate, "PPP") : "Select end date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={(date) => {
                            setEndDate(date);
                            setFormData({ 
                              ...formData, 
                              end_date: date ? format(date, "yyyy-MM-dd") : ""
                            });
                          }}
                          disabled={(date) => date < selectedDate}
                        />
                      </PopoverContent>
                    </Popover>
                    {endDate && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs text-red-500 mt-1"
                        onClick={() => {
                          setEndDate(null);
                          setFormData({ ...formData, end_date: "" });
                        }}
                      >
                        Clear end date
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="input-label">Select Work Dates</Label>
                  <p className="text-xs text-gray-500 mb-2">Click on dates to select/deselect. Holidays marked in red, weekends in orange. Backdated requests allowed up to 30 days.</p>
                  <DateSelectionCalendar
                    mode="custom"
                    selectedDates={customSelectedDates}
                    onSelectedDatesChange={(dates) => {
                      setCustomSelectedDates(dates);
                      // Update num_days based on selection
                      setFormData({ ...formData, num_days: dates.length });
                    }}
                    minDate={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)}
                    maxDatesWarning={30}
                  />
                </div>
              )}
              
              {/* Work on Weekends Option - only show for consecutive mode */}
              {dateSelectionMode === "consecutive" && (
                <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <Label className="input-label">Work on Weekends?</Label>
                    <p className="text-xs text-gray-500">
                      If yes, Sat/Sun are charged at holiday rate. If no, weekends are excluded from count.
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="work_on_weekends"
                        checked={!formData.work_on_weekends}
                        onChange={() => setFormData({ ...formData, work_on_weekends: false })}
                        className="w-4 h-4 text-[#F37021]"
                      />
                      <span className="text-sm">No</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="work_on_weekends"
                        checked={formData.work_on_weekends}
                        onChange={() => setFormData({ ...formData, work_on_weekends: true })}
                        className="w-4 h-4 text-[#F37021]"
                      />
                      <span className="text-sm">Yes (holiday rate)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Country Selection for Traktamente */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-green-600" />
                  <Label className="input-label">Work Location Country</Label>
                </div>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sweden">Sweden</SelectItem>
                    <SelectItem value="Norway">Norway</SelectItem>
                    <SelectItem value="Denmark">Denmark</SelectItem>
                    <SelectItem value="Finland">Finland</SelectItem>
                    <SelectItem value="Iceland">Iceland</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.country !== "Sweden" && (
                  <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Traktamente Notice</p>
                      <p className="text-xs text-yellow-700">
                        Work outside Sweden may require traktamente (daily allowance) payments. 
                        This will be noted in the quote.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Project Selection */}
              <div>
                <Label className="input-label">A-Number/Agreement *</Label>
                <Select
                  value={formData.agreement_id}
                  onValueChange={(value) => {
                    // Find the selected agreement to auto-fill location fields
                    const selectedAgreement = agreements.find(a => a.id === value);
                    if (selectedAgreement) {
                      setFormData({ 
                        ...formData, 
                        agreement_id: value,
                        // Auto-fill from agreement (user can still override)
                        address: selectedAgreement.location || "",
                        location_name: selectedAgreement.project_name || ""
                      });
                    } else {
                      setFormData({ ...formData, agreement_id: value });
                    }
                  }}
                  required
                >
                  <SelectTrigger data-testid="agreement-select">
                    <SelectValue placeholder="Select A-number" />
                  </SelectTrigger>
                  <SelectContent>
                    {agreements.length === 0 ? (
                      <SelectItem value="none" disabled>No agreements available - create one first</SelectItem>
                    ) : (
                      agreements.map((agreement) => (
                        <SelectItem key={agreement.id} value={agreement.id}>
                          <span className="font-bold text-[#F37021]">{agreement.a_number}</span> - {agreement.project_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {agreements.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Create an A-number first in the Agreements page
                  </p>
                )}
              </div>

              {/* Location Details */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <Label className="input-label text-lg">Work Location Details</Label>
                
                <div>
                  <Label className="input-label text-xs">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full street address"
                    data-testid="worker-request-address-input"
                  />
                  {formData.agreement_id && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Auto-filled from A-number — edit if the work location differs
                    </p>
                  )}
                </div>

                <div>
                  <Label className="input-label text-xs">Location/Site Name</Label>
                  <Input
                    value={formData.location_name}
                    onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                    placeholder="e.g., Site A, Building 3, Warehouse"
                    data-testid="worker-request-location-name-input"
                  />
                  {formData.agreement_id && (
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Auto-filled from A-number — edit if needed
                    </p>
                  )}
                </div>

                <div>
                  <Label className="input-label text-xs">Contact Person Onsite</Label>
                  <Select
                    value={formData.contact_person_onsite_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, contact_person_onsite_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contact person" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No contact assigned</SelectItem>
                      {workers.filter(w => w.user_type === "worker" || w.user_type === "admin").map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name} {w.phone ? `(${w.phone})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="input-label text-xs">Project Engineer in Charge</Label>
                  <Select
                    value={formData.project_engineer_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, project_engineer_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project engineer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No engineer assigned</SelectItem>
                      {workers.filter(w => w.user_type === "worker" || w.user_type === "admin").map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name} {w.phone ? `(${w.phone})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="input-label">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the work, skills needed, etc."
                  rows={3}
                />
              </div>

              {/* Requirements Section */}
              <div className="border rounded-lg p-4 space-y-4">
                <Label className="input-label text-lg">Any Requirements?</Label>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="id06"
                      checked={formData.requirements.id06_card}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        requirements: { ...formData.requirements, id06_card: checked }
                      })}
                    />
                    <Label htmlFor="id06" className="font-medium">ID06 Card</Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="identification"
                      checked={formData.requirements.identification}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        requirements: { ...formData.requirements, identification: checked }
                      })}
                    />
                    <Label htmlFor="identification" className="font-medium">ID (Identification)</Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="ssg"
                      checked={formData.requirements.ssg_card}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        requirements: { ...formData.requirements, ssg_card: checked }
                      })}
                    />
                    <Label htmlFor="ssg" className="font-medium">SSG Card</Label>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="induction"
                      checked={formData.requirements.induction_required}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        requirements: { ...formData.requirements, induction_required: checked }
                      })}
                    />
                    <Label htmlFor="induction" className="font-medium">Induction Required</Label>
                  </div>

                  {/* Induction Details */}
                  {formData.requirements.induction_required && (
                    <div className="ml-6 p-4 bg-gray-50 rounded-lg space-y-3">
                      <div>
                        <Label className="input-label text-xs">Induction Type/Name</Label>
                        <Input
                          value={formData.requirements.induction_details.induction_type}
                          onChange={(e) => setFormData({
                            ...formData,
                            requirements: {
                              ...formData.requirements,
                              induction_details: {
                                ...formData.requirements.induction_details,
                                induction_type: e.target.value
                              }
                            }
                          })}
                          placeholder="e.g., Site Safety Induction"
                        />
                      </div>
                      <div>
                        <Label className="input-label text-xs">When Needed</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start">
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {inductionDate ? format(inductionDate, "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={inductionDate}
                              onSelect={setInductionDate}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  )}

                  {/* Other Requirements */}
                  <div>
                    <Label className="input-label text-xs mt-4">Other Requirements (Optional)</Label>
                    <Textarea
                      value={formData.requirements.other_requirements}
                      onChange={(e) => setFormData({
                        ...formData,
                        requirements: { ...formData.requirements, other_requirements: e.target.value }
                      })}
                      placeholder="Any other specific requirements..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
              
              {/* File Attachments Section - only show when editing existing request */}
              {editingRequest && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-[#F37021]" />
                    <Label className="input-label">Attachments (Job specs, drawings, induction docs)</Label>
                  </div>
                  
                  <FileUploadZone
                    onUpload={handleFileUpload}
                    uploading={uploadingFiles}
                    progress={uploadProgress}
                    multiple={true}
                  />
                  
                  {requestAttachments.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Attached Files ({requestAttachments.length})
                      </p>
                      <AttachmentList
                        attachments={requestAttachments}
                        onDelete={handleAttachmentDelete}
                        canDelete={true}
                        currentUserId={user?.id}
                        showUploader={true}
                      />
                    </div>
                  )}
                  
                  {!editingRequest && (
                    <p className="text-xs text-gray-500 italic">
                      Save the request first, then edit it to add file attachments.
                    </p>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full btn-primary">
                <Send className="w-4 h-4 mr-2" />
                {editingRequest 
                  ? 'Update Request' 
                  : requestType === "contractor" 
                    ? 'Send Request to Gavs Bemanning AB' 
                    : 'Assign Internal Team Members'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Universal Search & Filters Section */}
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div className="flex-1 min-w-[250px] max-w-[400px]">
          <Label className="text-xs text-gray-500 uppercase mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by A-number, PO, project, worker, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9"
              data-testid="universal-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="without-po"
            checked={filterWithoutPO}
            onCheckedChange={setFilterWithoutPO}
            data-testid="filter-without-po"
          />
          <Label htmlFor="without-po" className="text-sm cursor-pointer">
            Show only without PO
          </Label>
        </div>
        {(searchQuery || filterWithoutPO) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setFilterWithoutPO(false);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : (
            <Table className="table-aggreko">
              <TableHeader>
                <TableRow>
                  <TableHead>A-NUMBER</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Work Date</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Needed</TableHead>
                  <TableHead>Assigned Workers</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Quote</TableHead>
                  <TableHead>PO</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>
                      {req.agreement_a_number ? (
                        <div>
                          <span className="font-bold text-[#F37021]">{req.agreement_a_number}</span>
                          <p className="text-xs text-gray-500">{req.agreement_project_name}</p>
                          <div className="flex gap-1 flex-wrap mt-1">
                            {req.is_direct_booking && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Direct Book</Badge>
                            )}
                            {req.is_backdated && (
                              <Badge data-testid="badge-backdated" className="bg-blue-100 text-blue-700 text-xs">Backdated</Badge>
                            )}
                            {req.requires_traktamente && (
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">Traktamente</Badge>
                            )}
                            {req.country && req.country !== "Sweden" && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">{req.country}</Badge>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-500">{req.created_at ? req.created_at.split('T')[0] : '-'}</span>
                    </TableCell>
                    <TableCell>{req.start_date}</TableCell>
                    <TableCell>
                      {req.project_name ? (
                        <div>
                          <p className="font-medium">{req.project_name}</p>
                          <p className="text-xs text-gray-500">#{req.project_number}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-bold text-lg text-[#F37021]">{req.num_workers}</TableCell>
                    <TableCell>
                      {req.worker_confirmations && req.worker_confirmations.length > 0 ? (
                        <div className="space-y-1">
                          {req.worker_confirmations.map((conf) => (
                            <div key={conf.worker_id || conf.worker_name} className="flex items-center gap-1 text-sm">
                              {conf.confirmed ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-500" />
                              )}
                              <span className={conf.confirmed ? "text-green-700" : "text-amber-700"}>
                                {conf.worker_name}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>{req.num_days}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.location_name || req.address}</p>
                        {req.location_name && req.address && (
                          <p className="text-xs text-gray-500">{req.address}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(req.status)}>
                        {req.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {req.latest_quote_number ? (
                        <div className="text-center">
                          <p className="text-xs font-mono text-gray-600">{req.latest_quote_number}</p>
                          <Badge className={
                            req.latest_quote_status === 'accepted' ? 'bg-green-100 text-green-800' :
                            req.latest_quote_status === 'rejected' ? 'bg-red-100 text-red-800' :
                            req.latest_quote_status === 'expired' ? 'bg-gray-100 text-gray-600' :
                            req.latest_quote_status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {req.latest_quote_status || 'draft'}
                          </Badge>
                          {req.latest_quote_valid_until && (
                            <p className="text-xs text-gray-400 mt-1">→ {req.latest_quote_valid_until}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No quote</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {req.po_number ? (
                        <span className="font-mono text-sm text-gray-800">{req.po_number}</span>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Pending PO</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() => openViewDetails(req)}
                          title="View Details"
                          data-testid={`admin-view-details-${req.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-green-500 hover:text-green-700"
                          onClick={() => handleDownloadQuote(req)}
                          disabled={downloadingQuote === req.id}
                          title="Download Quote PDF"
                          data-testid={`admin-download-quote-${req.id}`}
                        >
                          {downloadingQuote === req.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => openEditDialog(req)}
                          title="Edit Request"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(req.id)}
                          title="Delete Request"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-[#A1A1AA]">
                      No team member requests yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">REQUEST DETAILS</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <Badge className={getStatusBadge(selectedRequest.status)}>
                  {selectedRequest.status?.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-gray-500">
                  Requested by {selectedRequest.created_by_name}
                </span>
              </div>

              {/* A-Number Info */}
              {selectedRequest.agreement_a_number && (
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-xs text-orange-600 uppercase mb-1">A-Number</p>
                  <p className="text-xl font-bold text-[#F37021]">{selectedRequest.agreement_a_number}</p>
                  <p className="text-sm text-orange-700">{selectedRequest.agreement_project_name}</p>
                  {/* Badges for direct booking, backdated, traktamente, country */}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {selectedRequest.is_direct_booking && (
                      <Badge className="bg-green-100 text-green-700">Direct Booking</Badge>
                    )}
                    {selectedRequest.is_backdated && (
                      <Badge className="bg-blue-100 text-blue-700">Backdated Assignment</Badge>
                    )}
                    {selectedRequest.country && selectedRequest.country !== "Sweden" && (
                      <Badge className="bg-blue-100 text-blue-700">{selectedRequest.country}</Badge>
                    )}
                    {selectedRequest.requires_traktamente && (
                      <Badge className="bg-yellow-100 text-yellow-800">Traktamente Required</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Traktamente Notice */}
              {selectedRequest.requires_traktamente && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Traktamente Notice</p>
                      <p className="text-sm text-yellow-700">
                        This job is located in <strong>{selectedRequest.country}</strong> (outside Sweden).
                        Daily allowance (traktamente) payments may be required according to Swedish labor law.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Purchase Order (PO) Number - Admin can edit */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase font-medium">Purchase Order (PO)</p>
                  {selectedRequest.po_number && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={openPOHistory}
                      className="text-gray-500 hover:text-gray-700 h-6 px-2"
                      data-testid="po-history-btn"
                    >
                      <History className="w-3 h-3 mr-1" />
                      History
                    </Button>
                  )}
                </div>
                
                {editingPO ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={poValue}
                      onChange={(e) => setPOValue(e.target.value)}
                      placeholder="Enter PO number"
                      className="flex-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSavePO();
                        if (e.key === 'Escape') handleCancelPOEdit();
                      }}
                      data-testid="po-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleSavePO} 
                      disabled={savingPO}
                      className="btn-primary"
                      data-testid="po-save-btn"
                    >
                      {savingPO ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={handleCancelPOEdit}
                      data-testid="po-cancel-btn"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="flex items-center gap-2 cursor-pointer group"
                    onClick={() => setEditingPO(true)}
                    data-testid="po-display"
                  >
                    {selectedRequest.po_number ? (
                      <>
                        <span className="text-lg font-mono font-bold text-gray-800">{selectedRequest.po_number}</span>
                        <Edit className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </>
                    ) : (
                      <span className="text-gray-400 hover:text-gray-600 flex items-center gap-1">
                        <span>Add PO number</span>
                        <Edit className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                )}
                
                {selectedRequest.po_added_at && (
                  <p className="text-xs text-gray-400 mt-1">
                    Added by {selectedRequest.po_added_by_name} on {new Date(selectedRequest.po_added_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Main Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Team Members Needed</p>
                  <p className="text-3xl font-bold text-[#F37021]">{selectedRequest.num_workers}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Number of Days</p>
                  <p className="text-3xl font-bold">{selectedRequest.num_days}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Start Date</p>
                  <p className="text-lg font-medium">{selectedRequest.start_date}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">{selectedRequest.end_date ? 'End Date' : 'Location'}</p>
                  <p className="text-lg font-medium">
                    {selectedRequest.end_date || selectedRequest.location_name || selectedRequest.address || '-'}
                  </p>
                </div>
              </div>
              
              {/* Day Breakdown (if available) */}
              {(selectedRequest.weekend_days > 0 || selectedRequest.holiday_days > 0) && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <p className="text-xs text-amber-700 uppercase mb-2 font-medium">Day Breakdown for Pricing</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Normal days (Mon-Fri)</p>
                      <p className="text-xl font-bold">{selectedRequest.normal_days || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Weekend days</p>
                      <p className="text-xl font-bold text-amber-600">{selectedRequest.weekend_days || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Public holidays</p>
                      <p className="text-xl font-bold text-red-600">{selectedRequest.holiday_days || 0}</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    {selectedRequest.work_on_weekends 
                      ? "⚠️ Includes weekend/holiday work at holiday rate" 
                      : "Weekends excluded from count"}
                  </p>
                </div>
              )}

              {/* Assigned Workers */}
              {selectedRequest.worker_confirmations && selectedRequest.worker_confirmations.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Assigned Team Members</p>
                  <div className="space-y-1">
                    {selectedRequest.worker_confirmations.map((conf) => (
                      <div key={conf.worker_id || conf.worker_name} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                        {conf.confirmed ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                        )}
                        <span>{conf.worker_name}</span>
                        {conf.confirmed && conf.confirmed_at && (
                          <span className="text-xs text-gray-400">
                            Confirmed {new Date(conf.confirmed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedRequest.description && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Description</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedRequest.description}</p>
                </div>
              )}

              {/* Contractor Response */}
              {selectedRequest.contractor_response && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-700 mb-2">Contractor Response</p>
                  <p className="text-sm text-green-800">{selectedRequest.contractor_response}</p>
                </div>
              )}

              {/* Attachments Section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="w-4 h-4 text-[#F37021]" />
                  <p className="text-sm font-medium text-gray-700">
                    Attachments ({requestAttachments.length})
                  </p>
                </div>
                
                {requestAttachments.length > 0 ? (
                  <AttachmentList
                    attachments={requestAttachments}
                    onDelete={null}
                    canDelete={false}
                    showUploader={true}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">No files attached to this request.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PO History Dialog */}
      <Dialog open={poHistoryDialog} onOpenChange={setPOHistoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#F37021]" />
              PO Number History
            </DialogTitle>
          </DialogHeader>
          
          {loadingPOHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          ) : poHistory.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {poHistory.map((entry) => (
                <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{entry.edited_by_name}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 line-through">
                      {entry.old_value || "(empty)"}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="font-mono font-bold text-gray-800">
                      {entry.new_value || "(empty)"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No history available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard Component
export default function AdminDashboard() {
  return (
    <DashboardLayout userType="admin">
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="agreements" element={<AdminAgreements />} />
        <Route path="workers" element={<WorkersManagement />} />
        <Route path="timesheets" element={<TimesheetsManagement />} />
        <Route path="requests" element={<WorkerRequestsManagement />} />
        <Route path="contractor-profile" element={<AdminContractorProfile />} />
        <Route path="messages" element={<AdminMessages />} />
      </Routes>
      <ChatWidget />
    </DashboardLayout>
  );
}
